import { useMutation } from '@apollo/client/react'
import type { Translate } from '@athena/i18n'
import {
  Button,
  ConfirmationDialog,
  FailureNotice,
  FieldMessage,
  LiveRegion,
  useTranslate,
} from '@athena/ui'
import { useState } from 'react'

import {
  ArchivePracticeDocument,
  MyPracticesDocument,
  PracticeDocument,
  RestorePracticeDocument,
} from '../../graphql/generated/operations.js'
import { useUserErrorText } from '../../lib/user-errors.js'

export interface ArchiveActionProps {
  practiceId: string
  archived: boolean
  /** Named in the confirmation, so a tutor with several open tabs knows which one they are retiring. */
  title: string
  assignedCount: number
  hasDraft?: boolean
}

type Move = 'archive' | 'restore'

/**
 * Archive a practice, or put it back.
 *
 * Archiving goes through a confirmation and restoring does not, because only
 * one of the two takes something away — and even that is recoverable, so the
 * confirmation is `caution`, never red: "Keep it active" is the recommended,
 * first-focused way back, and nothing on either button reads as a deletion.
 * The consequence names what survives: attempts and scores are history and
 * stay, which is the part a tutor cannot see from the button.
 *
 * The dialog stays open until the server has answered. A refusal, or a
 * request that never got an answer, is said inside it, where the tutor is
 * looking, and pressing "Archive practice" again is the retry — which is safe:
 * archiving an archived practice changes nothing. Only a real archive closes
 * it, and then the page itself is the confirmation: the archived notice
 * appears and this button becomes Restore, both read from the refetched
 * practice, and the change is said aloud as well, since a notice that appears
 * with its text already in it is often not read.
 *
 * Restoring runs at once, and says a refusal or a failure beside its button,
 * with a retry of its own.
 */
export function ArchiveAction({
  practiceId,
  archived,
  title,
  assignedCount,
  hasDraft = false,
}: ArchiveActionProps) {
  const t = useTranslate()
  const userErrorText = useUserErrorText()
  // Both lists change shape when a practice moves between them, so the
  // practice list is refetched alongside the practice itself.
  const refetch = {
    refetchQueries: [PracticeDocument, MyPracticesDocument],
    awaitRefetchQueries: true,
  }
  const [archive] = useMutation(ArchivePracticeDocument, refetch)
  const [restore, { loading: restoring }] = useMutation(RestorePracticeDocument, refetch)

  const [open, setOpen] = useState(false)
  /** Why the last attempt at this move did not go through, in words or as a lost request. */
  const [outcome, setOutcome] = useState<{ refusal: string } | { error: unknown }>()
  const [announcement, setAnnouncement] = useState('')

  /** Resolves true only when the server has made the move. */
  async function move(which: Move): Promise<boolean> {
    setAnnouncement('')
    try {
      const variables = { practiceId }
      const payload =
        which === 'archive'
          ? (await archive({ variables })).data?.archivePractice
          : (await restore({ variables })).data?.restorePractice
      const userError = payload?.userErrors[0]
      if (userError || !payload?.practice) {
        setOutcome({
          refusal: userError
            ? userErrorText(userError)
            : t(
                which === 'archive'
                  ? 'practice.archive.archiveFailed'
                  : 'practice.archive.restoreFailed',
              ),
        })
        return false
      }
      setOutcome(undefined)
      setAnnouncement(
        t(which === 'archive' ? 'practice.archive.archived' : 'practice.archive.restored'),
      )
      return true
    } catch (error) {
      setOutcome({ error })
      return false
    }
  }

  function feedback(which: Move) {
    if (!outcome) return null
    if ('refusal' in outcome) return <FieldMessage tone="error">{outcome.refusal}</FieldMessage>
    return (
      <FailureNotice
        title={t(
          which === 'archive' ? 'practice.archive.archiveFailed' : 'practice.archive.restoreFailed',
        )}
        error={outcome.error}
        // Inside the dialog, going ahead again is the retry; beside Restore,
        // the notice offers its own.
        {...(which === 'restore'
          ? { retrying: restoring, onRetry: () => void move('restore') }
          : {})}
      />
    )
  }

  if (archived) {
    return (
      <div className="flex flex-col items-start gap-2">
        {hasDraft ? (
          <FieldMessage>{t('practice.newVersion.publishBeforeRestore')}</FieldMessage>
        ) : (
          <Button
            variant="secondary"
            onClick={() => {
              setOutcome(undefined)
              void move('restore')
            }}
            loading={restoring}
            loadingText={t('practice.archive.restoring')}
          >
            {t('practice.archive.restore')}
          </Button>
        )}
        {feedback('restore')}
        <LiveRegion message={announcement} />
      </div>
    )
  }

  return (
    <div className="flex flex-col items-start gap-2">
      <ConfirmationDialog
        open={open}
        onOpenChange={(next) => {
          // A fresh start each time it opens: the last attempt's failure
          // belongs to that attempt.
          if (next) setOutcome(undefined)
          setOpen(next)
        }}
        intent="caution"
        title={t('practice.archive.confirmTitle', [title])}
        description={consequenceFor(t, assignedCount)}
        confirmLabel={t('practice.archive.archive')}
        confirmingLabel={t('practice.archive.archiving')}
        cancelLabel={t('practice.archive.keep')}
        failure={feedback('archive')}
        onConfirm={() => move('archive')}
        trigger={
          // Secondary and the default size, like the dialog's own confirm:
          // archiving is put away, not thrown away. It sits with the
          // practice's status, as something done to the practice, rather than
          // beside Publish at the same size, where it read as a second answer
          // to "what now?".
          <Button variant="secondary">{t('practice.archive.archive')}</Button>
        }
      />
      <LiveRegion message={announcement} />
    </div>
  )
}

/** Says what is lost and what is kept, in that order, and never "this cannot be undone" — it can. */
/**
 * What archiving costs, in one sentence.
 *
 * Three whole sentences in the dictionary rather than a count fragment plus a
 * shared tail. The tail used to be concatenated on, which works in English and
 * assumes every language puts the clause in the same place — the thing
 * section 18 rules out. Three keys is the price of not assuming it.
 */
function consequenceFor(t: Translate, assignedCount: number): string {
  if (assignedCount === 0) return t('practice.archive.consequenceNone')
  if (assignedCount === 1) return t('practice.archive.consequenceOne')
  return t('practice.archive.consequenceOther', [assignedCount])
}
